import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sun, Cloud, CloudRain, Users, TrendingUp, TrendingDown,
  Target, Euro, Armchair, UtensilsCrossed, Wine, AlertTriangle,
  CheckCircle, Calculator, RefreshCw, ThermometerSun, Umbrella
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Strategie = () => {
  const [wetter, setWetter] = useState("sonnig");
  const [strategie, setStrategie] = useState(null);
  const [wetterAnalyse, setWetterAnalyse] = useState(null);
  const [sitzplatzOptimierung, setSitzplatzOptimierung] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStrategie();
    fetchWetterAnalyse();
    fetchSitzplatzOptimierung();
  }, []);

  const fetchStrategie = async (selectedWetter = wetter) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/vollzeit-strategie`, {
        wetter: selectedWetter
      }, { withCredentials: true });
      setStrategie(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Fehler beim Laden der Strategie");
    } finally {
      setLoading(false);
    }
  };

  const fetchWetterAnalyse = async () => {
    try {
      const res = await axios.get(`${API}/tools/wetter-analyse`, { withCredentials: true });
      setWetterAnalyse(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchSitzplatzOptimierung = async () => {
    try {
      const res = await axios.get(`${API}/tools/sitzplatz-optimierung`, { withCredentials: true });
      setSitzplatzOptimierung(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleWetterChange = (newWetter) => {
    setWetter(newWetter);
    fetchStrategie(newWetter);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const WetterButton = ({ value, icon: Icon, label, color }) => (
    <button
      onClick={() => handleWetterChange(value)}
      className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
        wetter === value 
          ? `border-${color}-500 bg-${color}-50 text-${color}-700` 
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
      style={{
        borderColor: wetter === value ? (color === 'yellow' ? '#eab308' : color === 'gray' ? '#6b7280' : '#3b82f6') : undefined,
        backgroundColor: wetter === value ? (color === 'yellow' ? '#fefce8' : color === 'gray' ? '#f9fafb' : '#eff6ff') : undefined
      }}
    >
      <Icon size={32} className={wetter === value ? '' : 'text-stone-400'} 
        style={{ color: wetter === value ? (color === 'yellow' ? '#ca8a04' : color === 'gray' ? '#374151' : '#2563eb') : undefined }} />
      <span className="text-sm font-medium mt-2">{label}</span>
    </button>
  );

  return (
    <div data-testid="strategie-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Vollzeit-Strategie</h1>
            <p className="page-subtitle">BWA, Wetter, Sitzplätze & Preisoptimierung</p>
          </div>
          <Button 
            onClick={() => fetchStrategie(wetter)} 
            disabled={loading}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </header>

      <div className="px-8 py-4 space-y-6">
        {/* Wetter-Auswahl */}
        <div className="bg-white border border-stone-200 p-5">
          <h3 className="font-display text-base font-semibold text-[#3A4D39] mb-4 flex items-center gap-2">
            <ThermometerSun size={18} />
            Wetterlage wählen
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <WetterButton value="sonnig" icon={Sun} label="Sonnig" color="yellow" />
            <WetterButton value="bewoelkt" icon={Cloud} label="Bewölkt" color="gray" />
            <WetterButton value="regen" icon={CloudRain} label="Regen" color="blue" />
          </div>
        </div>

        {strategie && (
          <>
            {/* Prognose-Karten */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Umsatz-Prognose */}
              <div className="bg-white border border-stone-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-[#5C5C5C]">Umsatz heute</span>
                  <Euro size={16} className="text-green-600" />
                </div>
                <p className="text-2xl font-bold text-[#3A4D39]">
                  {formatCurrency(strategie.prognose_tag.erwarteter_umsatz)}
                </p>
                <p className="text-xs text-[#858585] mt-1">
                  Faktor: {strategie.wetter.umsatz_faktor}x ({strategie.wetter.aktuell})
                </p>
              </div>

              {/* Gäste-Prognose */}
              <div className="bg-white border border-stone-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-[#5C5C5C]">Gäste erwartet</span>
                  <Users size={16} className="text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-[#3A4D39]">
                  {strategie.prognose_tag.erwartete_gaeste}
                </p>
                <p className="text-xs text-[#858585] mt-1">
                  {strategie.prognose_tag.gaeste_pro_stunde} pro Stunde
                </p>
              </div>

              {/* Verfügbare Plätze */}
              <div className="bg-white border border-stone-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-[#5C5C5C]">Plätze verfügbar</span>
                  <Armchair size={16} className="text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-[#3A4D39]">
                  {strategie.kapazitaet.heute_verfuegbar}
                </p>
                <p className="text-xs text-[#858585] mt-1">
                  {strategie.kapazitaet.sitzplaetze_innen} innen + {strategie.wetter.terrasse_nutzbar ? strategie.kapazitaet.sitzplaetze_aussen : 0} Terrasse
                </p>
              </div>

              {/* Gewinn-Prognose */}
              <div className={`border p-4 ${strategie.prognose_tag.erwarteter_gewinn > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-[#5C5C5C]">Gewinn erwartet</span>
                  {strategie.prognose_tag.erwarteter_gewinn > 0 
                    ? <TrendingUp size={16} className="text-green-600" />
                    : <TrendingDown size={16} className="text-red-600" />
                  }
                </div>
                <p className={`text-2xl font-bold ${strategie.prognose_tag.erwarteter_gewinn > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(strategie.prognose_tag.erwarteter_gewinn)}
                </p>
                <p className="text-xs text-[#858585] mt-1">
                  nach {formatCurrency(strategie.prognose_tag.kosten_tag)} Kosten
                </p>
              </div>
            </div>

            {/* Terrassen-Info */}
            {!strategie.wetter.terrasse_nutzbar && (
              <div className="bg-blue-50 border border-blue-200 p-4 flex items-center gap-3">
                <Umbrella className="text-blue-600" size={24} />
                <div>
                  <p className="font-semibold text-blue-800">Außengastronomie nicht nutzbar</p>
                  <p className="text-sm text-blue-600">
                    Bei Regen stehen nur {strategie.kapazitaet.sitzplaetze_innen} Innenplätze zur Verfügung 
                    (statt {strategie.kapazitaet.sitzplaetze_gesamt} gesamt)
                  </p>
                </div>
              </div>
            )}

            <Tabs defaultValue="tagesstrategie" className="w-full">
              <TabsList className="bg-stone-100 p-1 mb-4">
                <TabsTrigger value="tagesstrategie" className="data-[state=active]:bg-white text-xs">
                  <Target size={14} className="mr-1" />Tagesstrategie
                </TabsTrigger>
                <TabsTrigger value="produkte" className="data-[state=active]:bg-white text-xs">
                  <Wine size={14} className="mr-1" />Produkt-Performance
                </TabsTrigger>
                <TabsTrigger value="bwa" className="data-[state=active]:bg-white text-xs">
                  <Calculator size={14} className="mr-1" />12-Monats-BWA
                </TabsTrigger>
                <TabsTrigger value="sitzplaetze" className="data-[state=active]:bg-white text-xs">
                  <Armchair size={14} className="mr-1" />Sitzplatz-Analyse
                </TabsTrigger>
              </TabsList>

              {/* Tagesstrategie */}
              <TabsContent value="tagesstrategie">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Break-Even */}
                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <Target size={16} className="text-amber-600" />
                      Break-Even heute
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5C5C5C]">Mindest-Gäste:</span>
                        <span className="font-bold">{strategie.break_even.gaeste_minimum} Gäste</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5C5C5C]">Mindest-Auslastung:</span>
                        <span className="font-bold">{strategie.break_even.auslastung_minimum}</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-3 mt-2">
                        <div 
                          className="bg-green-500 h-3 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (strategie.prognose_tag.erwartete_gaeste / strategie.break_even.gaeste_minimum) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-center text-[#858585]">
                        {strategie.prognose_tag.erwartete_gaeste} von {strategie.break_even.gaeste_minimum} Gästen erwartet
                      </p>
                    </div>
                  </div>

                  {/* Handlungsempfehlungen */}
                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      Handlungsempfehlungen
                    </h4>
                    <ul className="space-y-2">
                      {strategie.handlungsempfehlungen.filter(h => h).map((empfehlung, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{empfehlung}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Preisempfehlungen */}
                  <div className="bg-white border border-stone-200 p-5 md:col-span-2">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <Euro size={16} className="text-[#3A4D39]" />
                      Preisempfehlungen für heute
                    </h4>
                    {strategie.preisempfehlungen.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {strategie.preisempfehlungen.map((emp, i) => (
                          <div key={i} className={`p-4 rounded-lg ${
                            emp.typ === 'Aktion' ? 'bg-blue-50 border border-blue-200' :
                            emp.typ === 'Upselling' ? 'bg-green-50 border border-green-200' :
                            'bg-amber-50 border border-amber-200'
                          }`}>
                            <span className={`text-xs font-semibold uppercase ${
                              emp.typ === 'Aktion' ? 'text-blue-600' :
                              emp.typ === 'Upselling' ? 'text-green-600' :
                              'text-amber-600'
                            }`}>{emp.typ}</span>
                            <h5 className="font-semibold text-sm mt-1">{emp.titel}</h5>
                            <p className="text-xs text-[#5C5C5C] mt-1">{emp.beschreibung}</p>
                            {emp.aktueller_preis && (
                              <p className="text-xs mt-2">
                                <span className="line-through text-[#858585]">{formatCurrency(emp.aktueller_preis)}</span>
                                <span className="ml-2 font-bold text-green-600">{formatCurrency(emp.vorgeschlagener_preis)}</span>
                              </p>
                            )}
                            <p className="text-xs font-medium mt-2 text-[#3A4D39]">{emp.erwarteter_effekt}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#858585]">Keine speziellen Empfehlungen für heute.</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Produkt-Performance */}
              <TabsContent value="produkte">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top-Seller */}
                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <TrendingUp size={16} className="text-green-600" />
                      Top 5 - Beste Deckungsbeiträge
                    </h4>
                    <div className="space-y-3">
                      {strategie.produkt_performance.top_seller.map((p, i) => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold">
                              {i + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-[#858585]">{p.kategorie} • VK: {formatCurrency(p.verkaufspreis)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-700">{formatCurrency(p.deckungsbeitrag_tag)}/Tag</p>
                            <p className="text-xs text-[#858585]">Marge: {p.marge_prozent}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ladenhüter */}
                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <TrendingDown size={16} className="text-red-600" />
                      Flop 5 - Optimierungspotential
                    </h4>
                    <div className="space-y-3">
                      {strategie.produkt_performance.ladenhuter.map((p, i) => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                              {strategie.produkt_performance.gesamt_produkte - strategie.produkt_performance.ladenhuter.length + i + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-xs text-[#858585]">{p.kategorie} • VK: {formatCurrency(p.verkaufspreis)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-700">{formatCurrency(p.deckungsbeitrag_tag)}/Tag</p>
                            <p className="text-xs text-[#858585]">Marge: {p.marge_prozent}%</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <AlertTriangle size={12} className="inline mr-1" />
                        <strong>Tipp:</strong> Produkte mit hoher Marge aber geringem Absatz können durch Preissenkung oder bessere Platzierung gepusht werden.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* BWA 12 Monate */}
              <TabsContent value="bwa">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4">Jahresübersicht</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C5C5C]">Jahresumsatz:</span>
                        <span className="font-bold">{formatCurrency(strategie.bwa_12_monate.jahresumsatz)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C5C5C]">Ø Monat:</span>
                        <span className="font-bold">{formatCurrency(strategie.bwa_12_monate.durchschnitt_monat)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#5C5C5C]">Monate erfasst:</span>
                        <span className="font-bold">{strategie.bwa_12_monate.monate_erfasst}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <Sun size={16} className="text-yellow-500" />
                      Bei Sonnenschein
                    </h4>
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(strategie.bwa_12_monate.umsatz_bei_sonne)}
                    </p>
                    <p className="text-xs text-[#858585] mt-1">Ø Tagesumsatz</p>
                  </div>

                  <div className="bg-white border border-stone-200 p-5">
                    <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                      <CloudRain size={16} className="text-blue-500" />
                      Bei Regen
                    </h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(strategie.bwa_12_monate.umsatz_bei_regen)}
                    </p>
                    <p className="text-xs text-[#858585] mt-1">Ø Tagesumsatz</p>
                  </div>

                  {wetterAnalyse && wetterAnalyse.vergleich && (
                    <div className="bg-gradient-to-r from-yellow-50 to-blue-50 border border-stone-200 p-5 md:col-span-3">
                      <h4 className="font-semibold text-sm mb-2">Wetter-Vergleich</h4>
                      <p className="text-lg">
                        <span className="font-bold text-yellow-600">{wetterAnalyse.vergleich.sonnig_vs_regen}</span>
                        {" "}= {formatCurrency(wetterAnalyse.vergleich.umsatz_differenz)} mehr Umsatz pro Tag
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Sitzplatz-Analyse */}
              <TabsContent value="sitzplaetze">
                {sitzplatzOptimierung && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-stone-200 p-5">
                      <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                        <Armchair size={16} className="text-amber-600" />
                        Kapazität
                      </h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-2xl font-bold">{sitzplatzOptimierung.kapazitaet.innen}</p>
                          <p className="text-xs text-[#858585]">Innen</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">{sitzplatzOptimierung.kapazitaet.aussen}</p>
                          <p className="text-xs text-[#858585]">Terrasse</p>
                        </div>
                        <div className="p-3 bg-[#3A4D39] rounded-lg">
                          <p className="text-2xl font-bold text-white">{sitzplatzOptimierung.kapazitaet.gesamt}</p>
                          <p className="text-xs text-green-100">Gesamt</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-stone-200 p-5">
                      <h4 className="font-semibold text-sm mb-4">Kennzahlen pro Sitzplatz</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-[#5C5C5C]">Umsatz/Platz/Tag:</span>
                          <span className="font-bold">{formatCurrency(sitzplatzOptimierung.kennzahlen.umsatz_pro_platz_tag)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-[#5C5C5C]">Umsatz/Platz/Monat:</span>
                          <span className="font-bold">{formatCurrency(sitzplatzOptimierung.kennzahlen.umsatz_pro_platz_monat)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-[#5C5C5C]">Kosten/Platz/Tag:</span>
                          <span className="font-bold text-red-600">{formatCurrency(sitzplatzOptimierung.kennzahlen.kosten_pro_platz_tag)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Szenarien */}
                    <div className="bg-white border border-stone-200 p-5 md:col-span-2">
                      <h4 className="font-semibold text-sm mb-4">Szenarien</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sitzplatzOptimierung.szenarien.map((szenario, i) => (
                          <div key={i} className={`p-4 rounded-lg ${i === 0 ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                            <h5 className="font-semibold text-sm flex items-center gap-2">
                              {i === 0 ? <CloudRain size={16} className="text-blue-600" /> : <Sun size={16} className="text-yellow-600" />}
                              {szenario.name}
                            </h5>
                            <div className="mt-3 space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#5C5C5C]">Verfügbare Plätze:</span>
                                <span className="font-bold">{szenario.plaetze}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#5C5C5C]">Benötigt/Platz:</span>
                                <span className="font-bold">{formatCurrency(szenario.benoetigter_umsatz_pro_platz)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#5C5C5C]">Prognose:</span>
                                <span className="font-bold text-green-600">{formatCurrency(szenario.umsatz_tag_prognose)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Empfehlungen */}
                    <div className="bg-green-50 border border-green-200 p-4 md:col-span-2">
                      <h4 className="font-semibold text-sm mb-2 text-green-800">Empfehlungen</h4>
                      <ul className="space-y-1 text-sm text-green-700">
                        <li className="flex items-center gap-2">
                          <CloudRain size={14} /> <strong>Bei Regen:</strong> {sitzplatzOptimierung.empfehlung.bei_regen}
                        </li>
                        <li className="flex items-center gap-2">
                          <Sun size={14} /> <strong>Bei Sonne:</strong> {sitzplatzOptimierung.empfehlung.bei_sonne}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default Strategie;
